import { faHeartPulse, faRuler } from '@fortawesome/pro-regular-svg-icons';
import { faTriangleExclamation } from '@fortawesome/pro-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import Pet from '@spike/pet-model';
import clsx from 'clsx';
import { Chip } from 'components/UI/V3/Chip/Chip';
import styles from 'components/UI/V3/PetProfileCard/PetProfileCard.module.scss';
import { PetTypeIcon } from 'components/UI/V3/PetTypeIcon/PetTypeIcon';
import React from 'react';

interface PetProfileCardProps {
    pet: Pet;
    nameAction?: React.ReactNode;
    className?: string;
}

export const PetProfileCard: React.FC<PetProfileCardProps> = ({ pet, nameAction, className }) => {
    const showAvatar = React.useMemo(() => pet.image || pet.type, [pet.image, pet.type]);

    const petTags = React.useMemo(() => {
        const positiveTags = pet.positivePersonalities || [];
        const negativeTags = pet.negativePersonalities || [];

        return [...positiveTags, ...negativeTags].sort((a, b) => a.name.localeCompare(b.name));
    }, [pet.positivePersonalities, pet.negativePersonalities]);

    const hasNegativePersonalities = pet.negativePersonalities && pet.negativePersonalities.length > 0;

    return (
        <div className={clsx(styles.root, className)}>
            {showAvatar && (
                <div className={styles.avatarColumn}>
                    <div className={styles.avatar}>
                        {pet.image && (
                            <img
                                src={pet.image}
                                alt={pet.name}
                            />
                        )}

                        {!pet.image && pet.type && (
                            <PetTypeIcon
                                size={64}
                                type={pet.type}
                                variant="light"
                            />
                        )}
                    </div>
                </div>
            )}

            <div className={styles.profileColumn}>
                <div className={styles.name}>
                    <span>{pet.name}</span>

                    {pet.hasMedicalConditions && (
                        <FontAwesomeIcon
                            icon={faHeartPulse}
                            className={clsx(styles.icon, styles.medicalConditionsIcon)}
                        />
                    )}

                    {hasNegativePersonalities && (
                        <FontAwesomeIcon
                            icon={faTriangleExclamation}
                            className={clsx(styles.icon, styles.specialCareIcon)}
                        />
                    )}

                    {pet.deceased && <span className={clsx(styles.icon, styles.deceasedIcon)}>🌈</span>}

                    {nameAction && <div className={styles.nameAction}>{nameAction}</div>}
                </div>

                <div className={styles.infoRow}>
                    <div className={styles.infoItem}>
                        {pet.type && (
                            <PetTypeIcon
                                type={pet.type}
                                variant="unstyled"
                            />
                        )}

                        <span>{pet.breed?.name ?? '—'}</span>
                    </div>

                    <div className={styles.infoItem}>
                        <FontAwesomeIcon icon={faRuler} />

                        <span>{pet.size?.name ?? '—'}</span>
                    </div>
                </div>

                <div className={styles.tagsRow}>
                    {petTags.map(petTag => (
                        <Chip
                            key={petTag.id}
                            label={petTag.name}
                        />
                    ))}
                </div>
            </div>
        </div>
    );
};
